import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import './ContactForm.css';

const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaRef = useRef(null);

  // Catch reCAPTCHA script timeout errors so they don't trigger CRA error overlay
  useEffect(() => {
    const handleGlobalError = (event) => {
      if (event.message === 'Timeout' || event.message === 'Script error.') {
        event.preventDefault();
        console.warn('reCAPTCHA script failed to load (timeout or blocked). This is expected on localhost if a firewall is active.');
        setRecaptchaError(true);
      }
    };
    window.addEventListener('error', handleGlobalError);
    return () => window.removeEventListener('error', handleGlobalError);
  }, []);

  // Reset form when opening/closing
  useEffect(() => {
    if (!isOpen) {
      setFormData({ email: '', subject: '', message: '' });
      setSubmitStatus(null);
      setErrorMessage('');
      setRecaptchaError(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any previous error messages when user starts typing
    if (submitStatus === 'error') {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setErrorMessage('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    
    if (!formData.subject.trim()) {
      setErrorMessage('Subject is required');
      return false;
    }
    
    if (!formData.message.trim()) {
      setErrorMessage('Message is required');
      return false;
    }
    
    if (formData.message.length > 2000) {
      setErrorMessage('Message is too long (maximum 2000 characters)');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      setErrorMessage('Please complete the reCAPTCHA verification');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      console.log('🔍 ContactForm API URL Debug:', {
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        baseUrl: baseUrl,
        allEnvVars: Object.keys(process.env).filter(key => key.includes('API')).reduce((obj, key) => {
          obj[key] = process.env[key];
          return obj;
        }, {})
      });
      
      console.log('🔍 reCAPTCHA Debug:', {
        REACT_APP_RECAPTCHA_SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        usingTestKey: !process.env.REACT_APP_RECAPTCHA_SITE_KEY
      });
      
      // Remove trailing slash and /api if they exist to avoid double paths
      const cleanBaseUrl = baseUrl.replace(/\/+$/, '').replace(/\/api$/, '');
      const apiUrl = `${cleanBaseUrl}/api`;
      
      console.log('🔍 Final API URL:', `${apiUrl}/contact/submit`);
      
      const response = await axios.post(`${apiUrl}/contact/submit`, {
        ...formData,
        recaptchaToken
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ email: '', subject: '', message: '' });
        recaptchaRef.current?.reset();
        
        // Auto-close the form after 3 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        setErrorMessage(error.response.data.errors.join(', '));
      } else {
        setErrorMessage('Failed to send message. Please try again.');
      }
      
      // Reset reCAPTCHA on error
      recaptchaRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', subject: '', message: '' });
    setSubmitStatus(null);
    setErrorMessage('');
    setRecaptchaError(false);
    recaptchaRef.current?.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="contact-form-overlay" onClick={handleClose}>
      <div className="contact-form-container" onClick={(e) => e.stopPropagation()}>
        <div className="contact-form-header">
          <h2>Contact Us</h2>
          <button className="close-button" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        {submitStatus === 'success' ? (
          <div className="contact-form-success">
            <div className="success-icon">✓</div>
            <h3>Message Sent Successfully!</h3>
            <p>Thank you for your message. We'll get back to you soon.</p>
            <p className="auto-close-note">This form will close automatically in a few seconds.</p>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="What's this about?"
                maxLength="200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us how we can help you..."
                rows="6"
                maxLength="2000"
                required
                disabled={isSubmitting}
              />
              <div className="character-count">
                {formData.message.length}/2000 characters
              </div>
            </div>

            <div className="form-group recaptcha-group">
              {recaptchaError ? (
                <div className="recaptcha-error">
                  <p>reCAPTCHA failed to load. Please refresh the page and try again.</p>
                  <button 
                    type="button" 
                    onClick={() => setRecaptchaError(false)}
                    className="retry-button"
                  >
                    Retry reCAPTCHA
                  </button>
                </div>
              ) : (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                  onChange={() => {
                    // Clear errors when reCAPTCHA is completed
                    if (submitStatus === 'error') {
                      setSubmitStatus(null);
                      setErrorMessage('');
                    }
                  }}
                  onExpired={() => {
                    console.log('reCAPTCHA expired');
                  }}
                  onError={() => {
                    console.log('reCAPTCHA error occurred');
                    setRecaptchaError(true);
                  }}
                />
              )}
            </div>

            {submitStatus === 'error' && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {errorMessage}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button" 
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;